import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { BankAccountMembershipMembersDatatableDocument } from 'generated/resources/datatable.graphql'
import {
  AccessForSelectFragment,
  AccountPageDocument,
  AddBankAccountMembershipMemberDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { BankAccountMembershipSelector } from '../BankAccountMembershipSelector'

interface AddBankAccountMembershipModalProps {
  onClose: () => void
  bankAccountId: number
}

const AddBankAccountMembershipModal = ({
  onClose,
  bankAccountId,
}: AddBankAccountMembershipModalProps) => {
  const { company } = useCurrentSession()
  const [selectedAccesses, setSelectedAccesses] = React.useState<AccessForSelectFragment[]>()

  const [addMembership] = useMutation(AddBankAccountMembershipMemberDocument, {
    refetchQueries: [BankAccountMembershipMembersDatatableDocument, AccountPageDocument],
    extractorFn: (data) => data.bankingMutations.upsertBankingBankAccountMembership,
  })

  const handleSelectAccesses = (newAccesses: AccessForSelectFragment[]) => {
    setSelectedAccesses(newAccesses)
  }

  const handleSubmit = async () => {
    track(trackingEvents.accounting.banking.addPeopleBankAccount)
    const result = await addMembership({
      variables: {
        accessIds: selectedAccesses?.map((access) => access.id) || [],
        companyId: company.id,
        bankAccountId: bankAccountId,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      onClose()
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  return (
    <Modal
      title={i18n.t('banking.accounts.add_membership_modal.title')}
      description={i18n.t('banking.accounts.add_membership_modal.description')}
      illustration={'add-user'}
      onClose={onClose}
      footer={
        <Box flexDirection="row" gap="s8">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={selectedAccesses?.length == 0}>
            {i18n.t('common.add')}
          </Button>
        </Box>
      }
    >
      <Box flexDirection="column" gap="s24">
        <BankAccountMembershipSelector
          selectedAccesses={selectedAccesses}
          handleSelectAccesses={handleSelectAccesses}
        />
      </Box>
    </Modal>
  )
}

export default AddBankAccountMembershipModal
